import Dropzone, { type DropzoneRef } from "@/components/ui/baseUi/dropzone"
import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { type MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import { MediaType } from "@/modules/common/media/mediaTypes"
import { cn } from "@/modules/lib/utils"
import { useUploadMedia } from "@/modules/ui/components/fileUpload/hooks/useUploadMedia"
import { word } from "@modules/core/utils/i18n"
import { DOCUMENT_MAX_SIZE } from "@modules/ui/components/fileUpload/fileUploadUtils"
import { useRef } from "react"

export type FileUploadDocumentProps = {
  setDocument?: (i: MediaItemFormType | null) => void
  onStartUpload?: () => void
  onEndUpload?: () => void
  disabled?: boolean
  uploadDocument?: boolean
  acceptedTypes?: string
} & React.ComponentProps<"div">

export function FileUploadDocument({
  setDocument,
  onStartUpload,
  onEndUpload,
  className,
  disabled,
  children,
  acceptedTypes,
}: FileUploadDocumentProps) {
  const { uploadMedia } = useUploadMedia()
  const fileUploadRef = useRef<DropzoneRef>(null)

  const onUploadNewDocument = (file: File) => {
    if (!acceptedTypes?.includes(file.type)) return

    const newMedia: MediaItemFormType = {
      file: file,
      url: "",
      key: "",
      type: file.type.includes("pdf") ? MediaType.S3_PDF : MediaType.S3_IMAGE,
      isUploading: true,
      filename: file.name.replace(/\.pdf$/i, ""),
    }

    setDocument?.(newMedia)

    uploadMedia(file)
      .then((response) => {
        const newMediaItem = {
          ...newMedia,
          key: response.awsS3Key ?? null,
          isUploading: false,
        }

        setDocument?.(newMediaItem)
        onEndUpload?.()
      })
      .catch((error) => {
        if (error instanceof Error) {
          console.error(`[FileUploadDocument] ${error.message ?? word("global.error.label")}`)
        } else {
          console.error(`[FileUploadDocument] ${String(error)}`)
        }
        toast.error(word("global.error.label"), {
          description: word("global.fileUpload.error"),
          classNames: errorToastClassName(),
        })

        setDocument?.(null)
        onEndUpload?.()
      })
  }

  const onDropFiles = (files: File[]) => {
    try {
      onStartUpload?.()
      const documentFile = files[0]
      if (documentFile && documentFile.size > DOCUMENT_MAX_SIZE) {
        toast.error(word("global.error.label"), {
          description: word("global.form.document.max_size.error", { maxSize: DOCUMENT_MAX_SIZE / 1_000_000 }),
          classNames: errorToastClassName(),
        })
        onEndUpload?.()
        return
      }

      if (documentFile) {
        onUploadNewDocument(documentFile)
      } else {
        toast.error(word("global.error.label"), {
          description: word("global.fileUpload.error"),
          classNames: errorToastClassName(),
        })
        onEndUpload?.()
      }
    } catch (e) {
      if (e instanceof Error) {
        console.error(`[FileUploadDocument] ${e.message ?? word("global.error.label")}`)
      } else {
        console.error(`[FileUploadDocument] ${String(e)}`)
      }
      toast.error(word("global.error.label"), {
        description: word("global.fileUpload.error"),
        classNames: errorToastClassName(),
      })
      onEndUpload?.()
    }
  }

  return (
    <Dropzone
      ref={fileUploadRef}
      onFilesAdded={onDropFiles}
      className={cn(
        "relative w-full transition-opacity",
        disabled && "cursor-default",
        !disabled && "**:cursor-pointer",
        className,
      )}
      disabled={disabled}
      accept={acceptedTypes}
    >
      {children}
    </Dropzone>
  )
}
